import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  ArrowLeft, User, Clock, DollarSign, MessageSquare, Save, Plus, Pencil,
  Trash2, Download, Send, RefreshCw, Calendar, FileText, AlertCircle, Mail,
  CheckCircle2, X, TrendingUp, Award, AlertTriangle as AlertIcon, KeyRound, Lock, Copy,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { party_host: 'Party Host', manager: 'Manager', staff: 'Staff' };
const EMPLOYMENT_LABELS = { full_time: 'Full-time', part_time: 'Part-time', casual: 'Casual / On-call' };
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const WEEKDAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const todayIso = () => new Date().toISOString().slice(0, 10);
const addDaysIso = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const TABS = [
  { key: 'profile', label: 'Profile', icon: User },
  { key: 'time', label: 'Time Entries', icon: Clock },
  { key: 'payroll', label: 'Payroll', icon: DollarSign },
  { key: 'performance', label: 'Performance', icon: TrendingUp },
  { key: 'messages', label: 'Messages', icon: MessageSquare },
];

const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

// Admin-initiated PIN / password reset — one step, no admin PIN required.
const CredentialResetDialog = ({ open, onOpenChange, employee, kind }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [tempValue, setTempValue] = useState('');
  useEffect(() => { if (open) { setReason(''); setTempValue(''); } }, [open]);
  const label = kind === 'pin' ? 'PIN' : 'password';
  const submit = async () => {
    setBusy(true);
    try {
      const ep = kind === 'pin' ? 'reset-pin' : 'reset-password';
      const r = await axios.post(`${API_URL}/api/admin/employees/${employee.user_id}/${ep}`,
        { reason: reason.trim() }, { withCredentials: true });
      const temp = kind === 'pin' ? r.data?.temp_pin : r.data?.temp_password;
      const emailed = (r.data?.email_status || '').startsWith('sent');
      if (temp) setTempValue(temp);
      toast.success(emailed
        ? `Temporary ${label} emailed to ${employee.email}`
        : `${label[0].toUpperCase() + label.slice(1)} reset — share the temporary ${label} shown below with the employee.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to reset ${label}`);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid={`emp-reset-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>Reset {label}</DialogTitle>
          <DialogDescription>
            A temporary {label} will be generated and emailed to {employee?.name}. They'll be prompted to set a new one on next login, and all their sessions end immediately.
          </DialogDescription>
        </DialogHeader>
        {tempValue ? (
          <div className="space-y-3">
            <div className="rounded-xl border border-[#06D6A0]/40 bg-[#EAF7F2] p-4 text-center">
              <p className="text-xs text-[#6B6B7A] mb-1">Temporary {label} (share securely)</p>
              <p className="text-2xl font-bold font-mono tracking-widest text-[#1E1E24]" data-testid={`emp-reset-${kind}-value`}>{tempValue}</p>
              <Button variant="outline" size="sm" className="mt-3 rounded-full" data-testid={`emp-reset-${kind}-copy`}
                onClick={async () => { try { await navigator.clipboard.writeText(tempValue); toast.success('Copied to clipboard'); } catch { toast.error('Could not copy — select and copy manually'); } }}>
                <Copy className="w-3 h-3 mr-1" /> Copy {label}
              </Button>
            </div>
            <p className="text-xs text-[#6B6B7A] text-center">The employee signs in with this temporary {label} and will be asked to set a new one right away. Their account is active and any lock has been cleared.</p>
            <Button onClick={() => onOpenChange(false)} className="w-full rounded-full bg-[#1E1E24] text-white" data-testid={`emp-reset-${kind}-done`}>Done</Button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Reason (optional)</Label>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                  placeholder={`Why are you resetting this ${label}? (for the audit log)`} className="mt-1" data-testid={`emp-reset-${kind}-reason`} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid={`emp-reset-${kind}-cancel`}>Cancel</Button>
              <Button onClick={submit} disabled={busy}
                className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid={`emp-reset-${kind}-submit`}>
                {busy ? 'Resetting…' : `Reset ${label}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// -------------------------------------------------------------------
// Profile tab
// -------------------------------------------------------------------
const ProfileTab = ({ employee, onUpdated }) => {
  const [form, setForm] = useState(employee);
  const [saving, setSaving] = useState(false);
  const [resetKind, setResetKind] = useState(null);
  useEffect(() => {
    // Backend stores address as {street, city, province, postal_code}. Keep it
    // structured so the 4 inputs round-trip cleanly through PATCH.
    const addr = employee?.address;
    const addrObj = (addr && typeof addr === 'object')
      ? addr
      : (typeof addr === 'string' && addr.trim())
        ? (() => {
            const parts = addr.split(',').map((s) => s.trim());
            return { street: parts[0] || '', city: parts[1] || '', province: parts[2] || '', postal_code: parts[3] || '' };
          })()
        : { street: '', city: '', province: '', postal_code: '' };
    setForm({ ...employee, address: addrObj });
  }, [employee]);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onAddrChange = (k, v) => setForm((f) => ({ ...f, address: { ...(f?.address || {}), [k]: v } }));
  const toggleDay = (d) => setForm((f) => {
    const cur = f?.availability || [];
    return { ...f, availability: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d] };
  });

  const save = async () => {
    setSaving(true);
    try {
      // Address: only include if at least one part is filled in (avoids 400 from required-fields check)
      const addr = form?.address || {};
      const hasAnyAddr = ['street','city','province','postal_code'].some((k) => (addr[k] || '').trim());
      const payload = {
        name: form.name,
        employee_role: form.employee_role,
        phone: form.phone,
        hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : 0,
        active: form.active !== false,
        date_of_birth: form.date_of_birth || null,
        start_date: form.start_date || null,
        employment_type: form.employment_type || null,
        emergency_contact_name: form.emergency_contact_name || '',
        emergency_contact_phone: form.emergency_contact_phone || '',
        sin_last4: (form.sin_last4 || '').toString().replace(/\D/g, '').slice(-4),
        notes: form.notes || '',
        ...((form.availability && form.availability.length) ? { availability: form.availability } : {}),
        ...(hasAnyAddr ? { address: {
          street: (addr.street || '').trim(),
          city: (addr.city || '').trim(),
          province: (addr.province || '').trim(),
          postal_code: (addr.postal_code || '').trim(),
        } } : {}),
      };
      await axios.patch(`${API_URL}/api/admin/employees/${employee.user_id}`, payload,
        { withCredentials: true });
      toast.success('Profile saved');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5" data-testid="emp-profile-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#1E1E24]">Employee Profile</h3>
        <Button onClick={save} disabled={saving}
          className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
          data-testid="emp-profile-save">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          { k: 'name', l: 'Full Name *', type: 'text' },
          { k: 'email', l: 'Email (read-only)', type: 'text', disabled: true },
          { k: 'phone', l: 'Phone', type: 'tel' },
          { k: 'employee_role', l: 'Role *', type: 'select',
            options: [['party_host','Party Host'], ['manager','Manager'], ['staff','Staff']] },
          { k: 'employment_type', l: 'Employment Type', type: 'select',
            options: [['full_time','Full-time'], ['part_time','Part-time'], ['casual','Casual / On-call']] },
          { k: 'hourly_rate', l: 'Hourly Rate (CAD) *', type: 'number', step: '0.01' },
          { k: 'start_date', l: 'Start Date', type: 'date' },
          { k: 'date_of_birth', l: 'Date of Birth', type: 'date' },
          { k: 'sin_last4', l: 'SIN — last 4 digits', type: 'text',
            hint: 'Only the last 4 digits are stored; shown masked on paystubs.' },
        ].map((f) => (
          <div key={f.k}>
            <Label className="text-xs text-[#1E1E24]">{f.l}</Label>
            {f.type === 'select' ? (
              <Select value={form?.[f.k] || ''} onValueChange={(v) => onChange(f.k, v)}>
                <SelectTrigger className="bg-white mt-1" data-testid={`emp-profile-${f.k}`}>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {f.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input
                type={f.type} step={f.step}
                value={form?.[f.k] ?? ''}
                onChange={(e) => onChange(f.k, e.target.value)}
                disabled={f.disabled}
                className="mt-1"
                data-testid={`emp-profile-${f.k}`}
              />
            )}
            {f.hint && <p className="text-[10px] text-[#6B6B7A] mt-1">{f.hint}</p>}
          </div>
        ))}
        <div className="sm:col-span-2 lg:col-span-3">
          <Label className="text-xs">Address</Label>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-1">
            <Input value={form?.address?.street || ''} onChange={(e) => onAddrChange('street', e.target.value)}
              placeholder="Street" className="sm:col-span-2 lg:col-span-2" data-testid="emp-profile-address-street" />
            <Input value={form?.address?.city || ''} onChange={(e) => onAddrChange('city', e.target.value)}
              placeholder="City" data-testid="emp-profile-address-city" />
            <Input value={form?.address?.province || ''} onChange={(e) => onAddrChange('province', e.target.value)}
              placeholder="Province (e.g. ON)" data-testid="emp-profile-address-province" />
            <Input value={form?.address?.postal_code || ''} onChange={(e) => onAddrChange('postal_code', e.target.value)}
              placeholder="Postal code" className="sm:col-span-2 lg:col-span-1" data-testid="emp-profile-address-postal" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Emergency Contact — Name</Label>
          <Input value={form?.emergency_contact_name || ''} onChange={(e) => onChange('emergency_contact_name', e.target.value)} className="mt-1" data-testid="emp-profile-ec-name" />
        </div>
        <div>
          <Label className="text-xs">Emergency Contact — Phone</Label>
          <Input value={form?.emergency_contact_phone || ''} onChange={(e) => onChange('emergency_contact_phone', e.target.value)} className="mt-1" data-testid="emp-profile-ec-phone" />
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <Label className="text-xs">Notes (HR / Admin only)</Label>
          <Textarea value={form?.notes || ''} onChange={(e) => onChange('notes', e.target.value)} rows={2} className="mt-1" data-testid="emp-profile-notes" />
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <Label className="text-xs text-[#1E1E24]">Availability — days this employee can work</Label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="emp-availability-chips">
            {WEEKDAYS.map((d) => {
              const on = (form?.availability || []).includes(d);
              return (
                <button type="button" key={d} onClick={() => toggleDay(d)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors inline-flex items-center gap-1 ${on ? 'bg-[#FF3B6B] text-white border-[#FF3B6B]' : 'bg-white text-[#6B6B7A] border-[#E8E5DF] hover:border-[#FF3B6B]'}`}
                  data-testid={`emp-availability-${d}`}>
                  {on && <CheckCircle2 className="w-3 h-3" />}{WEEKDAY_LABELS[d]}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-[#6B6B7A] mt-1">
            {(form?.availability || []).length
              ? `Available ${(form.availability).length} day(s) — tap to toggle, then Save changes.`
              : 'No availability set — tap days to add, then Save changes.'}
          </p>
        </div>
      </div>

      {/* Security — admin credential resets */}
      <div className="mt-4 pt-4 border-t border-[#E8E5DF]">
        <Label className="text-xs text-[#1E1E24]">Security</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          <Button variant="outline" className="rounded-full" onClick={() => setResetKind('pin')} data-testid="emp-reset-pin-btn">
            <KeyRound className="w-4 h-4 mr-1" /> Reset PIN
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => setResetKind('password')} data-testid="emp-reset-password-btn">
            <Lock className="w-4 h-4 mr-1" /> Reset Password
          </Button>
        </div>
        <p className="text-[10px] text-[#6B6B7A] mt-1">
          Requires your admin PIN + a reason. A temporary credential is emailed to the employee, who must change it on next login.
        </p>
      </div>

      <div className="mt-4 pt-4 border-t border-[#E8E5DF] flex flex-wrap items-center gap-3 text-xs text-[#6B6B7A]">
        <Badge variant="outline" className="text-xs font-semibold text-[#1E1E24]" data-testid="emp-profile-employee-number">
          Employee #: <code className="ml-1">{employee.employee_number || '—'}</code>
        </Badge>
        <Badge variant="outline" className="text-xs">User ID: <code className="ml-1">{employee.user_id}</code></Badge>
        <Badge variant="outline" className={`text-xs ${employee.is_clocked_in ? 'bg-green-50 text-green-700 border-green-200' : ''}`}>
          {employee.is_clocked_in ? '🟢 Clocked In' : 'Off duty'}
        </Badge>
        <Badge variant="outline" className="text-xs">{employee.has_pin ? '🔑 PIN set' : '⚠️ No PIN'}</Badge>
        {employee.locked && <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">🔒 Locked out</Badge>}
      </div>

      {resetKind && (
        <CredentialResetDialog open={!!resetKind} onOpenChange={(o) => !o && setResetKind(null)}
          employee={employee} kind={resetKind} />
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// Time Entries / Shifts tab
// -------------------------------------------------------------------
const TimeTab = ({ employee }) => {
  const [from, setFrom] = useState(addDaysIso(-14));
  const [to, setTo] = useState(todayIso());
  const [shifts, setShifts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editShift, setEditShift] = useState(null);
  const [createShiftOpen, setCreateShiftOpen] = useState(false);
  const [hoursSummary, setHoursSummary] = useState({ regular: 0, overtime: 0, total: 0 });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sh, te] = await Promise.all([
        axios.get(`${API_URL}/api/admin/shifts`, {
          params: { user_id: employee.user_id, start_date: from, end_date: to },
          withCredentials: true,
        }),
        axios.get(`${API_URL}/api/admin/time-entries`, {
          params: { user_id: employee.user_id, date_from: from, date_to: to },
          withCredentials: true,
        }),
      ]);
      setShifts(sh.data || []);
      setEntries(te.data || []);
      // Compute reg/OT hours using weekly 44h rule
      const byWeek = {};
      const dayTotals = {};
      (te.data || []).forEach((e) => {
        if (!e.date || !e.hours) return;
        dayTotals[e.date] = (dayTotals[e.date] || 0) + Number(e.hours || 0);
      });
      Object.entries(dayTotals).forEach(([d, h]) => {
        const dt = new Date(d + 'T00:00:00');
        const dow = dt.getDay(); // 0 Sun..6 Sat
        const mondayOffset = dow === 0 ? -6 : 1 - dow;
        const monday = new Date(dt); monday.setDate(dt.getDate() + mondayOffset);
        const wk = monday.toISOString().slice(0, 10);
        byWeek[wk] = (byWeek[wk] || 0) + h;
      });
      let reg = 0, ot = 0;
      Object.values(byWeek).forEach((h) => { if (h > 44) { reg += 44; ot += h - 44; } else { reg += h; } });
      setHoursSummary({ regular: Math.round(reg * 100) / 100, overtime: Math.round(ot * 100) / 100, total: Math.round((reg + ot) * 100) / 100 });
    } catch (e) {
      toast.error('Failed to load time data');
    } finally { setLoading(false); }
  }, [employee.user_id, from, to]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deleteShift = async (id) => {
    if (!window.confirm('Delete this shift?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/shifts/${id}`, { withCredentials: true });
      toast.success('Shift deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-3" data-testid="emp-time-tab">
      {/* Range + summary + add */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 max-w-[160px]" data-testid="emp-time-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 max-w-[160px]" data-testid="emp-time-to" />
          </div>
          <Button variant="outline" className="rounded-full" onClick={fetchAll} data-testid="emp-time-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-[#6B6B7A]">
            <span className="font-semibold text-[#1E1E24]">Total:</span> {hoursSummary.total}h
            <span className="mx-2">·</span>
            Regular: <span className="font-semibold text-[#06D6A0]">{hoursSummary.regular}h</span>
            <span className="mx-2">·</span>
            OT (1.5×): <span className="font-semibold text-[#FF3B6B]">{hoursSummary.overtime}h</span>
          </div>
          <Button onClick={() => setCreateShiftOpen(true)} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="emp-add-shift">
            <Plus className="w-4 h-4 mr-1" /> Add Shift
          </Button>
        </div>
      </div>

      {/* Scheduled shifts */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <div className="px-4 py-2 border-b border-[#E8E5DF] flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#6B6B7A]" />
          <p className="font-semibold text-sm">Scheduled Shifts ({shifts.length})</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Date</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead><TableHead>Hours</TableHead><TableHead>Role</TableHead><TableHead>Notes</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (<TableRow><TableCell colSpan={7} className="text-center py-6 text-xs text-[#6B6B7A]">Loading…</TableCell></TableRow>)
            : shifts.length === 0 ? (<TableRow><TableCell colSpan={7} className="text-center py-6 text-xs text-[#6B6B7A]">No shifts in this range.</TableCell></TableRow>)
            : shifts.map((s) => {
              const [sh, sm] = (s.start_time || '0:0').split(':').map(Number);
              const [eh, em] = (s.end_time || '0:0').split(':').map(Number);
              const hours = (((eh*60+em) - (sh*60+sm))/60).toFixed(2);
              return (
                <TableRow key={s.shift_id} data-testid={`shift-row-${s.shift_id}`}>
                  <TableCell className="font-medium">{s.date}</TableCell>
                  <TableCell className="font-mono">{s.start_time}</TableCell>
                  <TableCell className="font-mono">{s.end_time}</TableCell>
                  <TableCell>{hours}h</TableCell>
                  <TableCell className="text-xs capitalize">{(s.role || 'staff').replace(/_/g,' ')}</TableCell>
                  <TableCell className="text-xs text-[#6B6B7A] max-w-[200px] truncate">{s.notes || '—'}</TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => setEditShift(s)} className="text-[#6366F1] hover:text-[#4F46E5] mr-2" data-testid={`shift-edit-${s.shift_id}`}><Pencil className="w-4 h-4 inline" /></button>
                    <button onClick={() => deleteShift(s.shift_id)} className="text-red-500 hover:text-red-600" data-testid={`shift-delete-${s.shift_id}`}><Trash2 className="w-4 h-4 inline" /></button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Time entries */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <div className="px-4 py-2 border-b border-[#E8E5DF] flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#6B6B7A]" />
          <p className="font-semibold text-sm">Time Entries ({entries.length})</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Date</TableHead><TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead><TableHead>Hours</TableHead><TableHead>Status</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 ? (<TableRow><TableCell colSpan={5} className="text-center py-6 text-xs text-[#6B6B7A]">No time entries in this range.</TableCell></TableRow>)
            : entries.map((e) => (
              <TableRow key={e.entry_id || e._id || `${e.date}-${e.clock_in}`} data-testid={`entry-row-${e.entry_id || e.date}`}>
                <TableCell>{e.date}</TableCell>
                <TableCell className="font-mono text-xs">{e.clock_in ? new Date(e.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
                <TableCell className="font-mono text-xs">{e.clock_out ? new Date(e.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : <Badge variant="outline" className="text-[10px]">Active</Badge>}</TableCell>
                <TableCell className="font-semibold">{Number(e.hours || 0).toFixed(2)}h</TableCell>
                <TableCell className="text-xs">
                  {e.auto_clocked_out && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Auto closed</Badge>}
                  {e.deleted && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700">Deleted</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ShiftEditDialog
        open={!!editShift || createShiftOpen}
        onOpenChange={(o) => { if (!o) { setEditShift(null); setCreateShiftOpen(false); } }}
        employee={employee}
        shift={editShift}
        onSaved={() => { setEditShift(null); setCreateShiftOpen(false); fetchAll(); }}
      />
    </div>
  );
};

const ShiftEditDialog = ({ open, onOpenChange, employee, shift, onSaved }) => {
  const isEdit = !!shift;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(shift ? { ...shift } : { date: todayIso(), start_time: '10:00', end_time: '17:00', role: 'staff', notes: '' });
  }, [open, shift]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        user_id: employee.user_id,
        date: form.date, start_time: form.start_time, end_time: form.end_time,
        role: form.role || 'staff', notes: form.notes || '',
      };
      if (isEdit) {
        await axios.patch(`${API_URL}/api/admin/shifts/${shift.shift_id}`, payload, { withCredentials: true });
        toast.success('Shift updated');
      } else {
        await axios.post(`${API_URL}/api/admin/shifts`, payload, { withCredentials: true });
        toast.success('Shift created');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="shift-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Shift' : 'Add Shift'}</DialogTitle>
          <DialogDescription>For {employee.name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Date *</Label>
            <Input type="date" value={form.date || ''} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" data-testid="shift-form-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Start *</Label>
              <Input type="time" value={form.start_time || ''} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1" data-testid="shift-form-start" />
            </div>
            <div>
              <Label className="text-xs">End *</Label>
              <Input type="time" value={form.end_time || ''} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1" data-testid="shift-form-end" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={form.role || 'staff'} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="bg-white mt-1" data-testid="shift-form-role"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="party_host">Party Host</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" data-testid="shift-form-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="shift-form-save">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -------------------------------------------------------------------
// Payroll tab
// -------------------------------------------------------------------
const PayrollTab = ({ employee }) => {
  const [periodEnd, setPeriodEnd] = useState(todayIso());
  const [periodType, setPeriodType] = useState('biweekly');
  const [generating, setGenerating] = useState(false);
  const [paystubs, setPaystubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [emailingId, setEmailingId] = useState(null);

  const fetchPaystubs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/paystubs`, { withCredentials: true });
      const all = Array.isArray(r.data) ? r.data : (r.data?.paystubs || []);
      setPaystubs(all.filter((ps) => ps.employee_id === employee.user_id));
    } catch (e) {
      toast.error('Failed to load paystubs');
    } finally { setLoading(false); }
  }, [employee.user_id]);

  useEffect(() => { fetchPaystubs(); }, [fetchPaystubs]);

  const previewPaystub = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/paystubs/generate`, {
        employee_ids: [employee.user_id], period_end: periodEnd, period_type: periodType,
        deduction_pct: 0, preview_only: true, include_zero_hours: true,
      }, { withCredentials: true });
      setPreview(r.data?.paystubs?.[0] || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setGenerating(false); }
  };

  const generatePaystub = async () => {
    if (!window.confirm(`Generate ${periodType} paystub ending ${periodEnd} for ${employee.name}?`)) return;
    setGenerating(true);
    try {
      await axios.post(`${API_URL}/api/admin/paystubs/generate`, {
        employee_ids: [employee.user_id], period_end: periodEnd, period_type: periodType,
        deduction_pct: 0, preview_only: false, include_zero_hours: true,
      }, { withCredentials: true });
      toast.success('Paystub generated');
      setPreview(null);
      fetchPaystubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const downloadPdf = (psid) => {
    window.open(`${API_URL}/api/admin/paystubs/${psid}/pdf`, '_blank');
  };

  const emailPaystub = async (psid) => {
    if (!window.confirm(`Email this paystub to ${employee.email}?`)) return;
    setEmailingId(psid);
    try {
      await axios.post(`${API_URL}/api/admin/paystubs/${psid}/send`, {}, { withCredentials: true });
      toast.success('Paystub emailed to employee');
      fetchPaystubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email failed');
    } finally { setEmailingId(null); }
  };

  const deletePaystub = async (psid) => {
    if (!window.confirm('Delete this paystub? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/paystubs/${psid}`, { withCredentials: true });
      toast.success('Deleted');
      fetchPaystubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-3" data-testid="emp-payroll-tab">
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 space-y-3">
        <p className="text-sm font-semibold text-[#1E1E24]">Generate Paystub</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Period End</Label>
            <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="mt-1 max-w-[180px]" data-testid="payroll-period-end" />
          </div>
          <div>
            <Label className="text-xs">Period Type</Label>
            <Select value={periodType} onValueChange={setPeriodType}>
              <SelectTrigger className="bg-white mt-1 w-[160px]" data-testid="payroll-period-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="weekly">Weekly (52 / yr)</SelectItem>
                <SelectItem value="biweekly">Bi-weekly (26 / yr)</SelectItem>
                <SelectItem value="semimonthly">Semi-monthly (24)</SelectItem>
                <SelectItem value="monthly">Monthly (12)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="rounded-full" onClick={previewPaystub} disabled={generating} data-testid="payroll-preview">
            <FileText className="w-4 h-4 mr-1" /> Preview
          </Button>
          <Button className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" onClick={generatePaystub} disabled={generating} data-testid="payroll-generate">
            <Plus className="w-4 h-4 mr-1" /> {generating ? 'Working…' : 'Generate Paystub'}
          </Button>
        </div>
        {preview && (
          <div className="mt-3 rounded-xl border border-[#FF3B6B]/30 bg-[#FFF5F8] p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="payroll-preview-card">
            <div><span className="text-[#6B6B7A] text-xs">Hours</span><br/><b>{preview.regular_hours} reg · {preview.overtime_hours} OT</b></div>
            <div><span className="text-[#6B6B7A] text-xs">Gross</span><br/><b>{fmt$(preview.gross_pay)}</b></div>
            <div><span className="text-[#6B6B7A] text-xs">Deductions</span><br/><b>{fmt$(preview.deduction_amount)}</b></div>
            <div><span className="text-[#6B6B7A] text-xs">Net Pay</span><br/><b className="text-[#FF3B6B]">{fmt$(preview.net_pay)}</b></div>
            <div className="col-span-2 sm:col-span-4 text-[10px] text-[#6B6B7A]">
              CPP {fmt$(preview.cpp)} · EI {fmt$(preview.ei)} · Federal {fmt$(preview.federal_tax)} · Ontario {fmt$(preview.provincial_tax)}
              {preview.health_premium > 0 && ` · Health ${fmt$(preview.health_premium)}`}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <div className="px-4 py-2 border-b border-[#E8E5DF] flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-[#6B6B7A]" />
          <p className="font-semibold text-sm">Past Paystubs ({paystubs.length})</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Period</TableHead><TableHead>Hours</TableHead><TableHead>Gross</TableHead><TableHead>Deductions</TableHead><TableHead>Net</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (<TableRow><TableCell colSpan={7} className="text-center py-6 text-xs text-[#6B6B7A]">Loading…</TableCell></TableRow>)
            : paystubs.length === 0 ? (<TableRow><TableCell colSpan={7} className="text-center py-6 text-xs text-[#6B6B7A]">No paystubs yet — use the generator above.</TableCell></TableRow>)
            : paystubs.map((ps) => (
              <TableRow key={ps.paystub_id} data-testid={`paystub-row-${ps.paystub_id}`}>
                <TableCell className="text-xs">{ps.pay_period_start} → {ps.pay_period_end}</TableCell>
                <TableCell className="text-xs">{ps.total_hours}h</TableCell>
                <TableCell>{fmt$(ps.gross_pay)}</TableCell>
                <TableCell className="text-xs">{fmt$(ps.deduction_amount)}</TableCell>
                <TableCell className="font-semibold text-[#FF3B6B]">{fmt$(ps.net_pay)}</TableCell>
                <TableCell>
                  {ps.email_sent_at ? <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200"><Mail className="w-3 h-3 inline mr-0.5" />Emailed</Badge>
                  : ps.status === 'viewed' ? <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700"><CheckCircle2 className="w-3 h-3 inline mr-0.5" />Viewed</Badge>
                  : <Badge variant="outline" className="text-[10px]">Generated</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <button onClick={() => downloadPdf(ps.paystub_id)} className="text-[#6366F1] mr-2" title="Download PDF" data-testid={`paystub-pdf-${ps.paystub_id}`}><Download className="w-4 h-4 inline" /></button>
                  <button onClick={() => emailPaystub(ps.paystub_id)} disabled={emailingId === ps.paystub_id} className="text-[#06D6A0] mr-2 disabled:opacity-50" title="Email to employee" data-testid={`paystub-email-${ps.paystub_id}`}>
                    {emailingId === ps.paystub_id ? '…' : <Send className="w-4 h-4 inline" />}
                  </button>
                  <button onClick={() => deletePaystub(ps.paystub_id)} className="text-red-500" title="Delete" data-testid={`paystub-delete-${ps.paystub_id}`}><Trash2 className="w-4 h-4 inline" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------
// Performance tab
// -------------------------------------------------------------------
const PerformanceTab = ({ employee }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(6);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/employees/${employee.user_id}/performance`,
        { params: { months }, withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load performance');
    } finally { setLoading(false); }
  }, [employee.user_id, months]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="bg-white rounded-2xl border border-[#E8E5DF] p-8 text-center text-sm text-[#6B6B7A]">Loading performance…</div>;
  if (!data) return null;

  const att = data.attendance || {};
  const gradeColors = { A: '#06D6A0', B: '#22C55E', C: '#F59E0B', D: '#FF3B6B' };
  const gradeColor = gradeColors[att.grade] || '#6B6B7A';

  const stats = [
    { label: 'Attendance Score', value: att.score_pct != null ? `${att.score_pct}%` : '—', accent: gradeColor, sub: att.grade ? `Grade ${att.grade}` : 'No shifts yet' },
    { label: 'On-Time Arrivals', value: att.on_time || 0, accent: '#06D6A0', sub: `of ${att.scheduled || 0} shifts` },
    { label: 'Late Arrivals', value: att.late || 0, accent: '#F59E0B', sub: '>10 min after start' },
    { label: 'No-Shows', value: att.no_show || 0, accent: '#FF3B6B', sub: 'shifts missed' },
  ];

  const secondary = [
    { label: 'Hours Worked', value: `${data.hours?.total || 0}h`, sub: `Avg ${data.hours?.avg_per_shift || 0}h / shift` },
    { label: 'Parties Hosted', value: data.parties_hosted || 0, sub: 'Confirmed bookings' },
    { label: 'Auto-clockouts', value: data.auto_clockouts || 0, sub: 'Forgot to clock out' },
    { label: 'Completed Shifts', value: data.hours?.completed_shifts || 0, sub: 'Fully clocked in/out' },
  ];

  return (
    <div className="space-y-3" data-testid="emp-performance-tab">
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#1E1E24]">Performance Window</p>
          <p className="text-xs text-[#6B6B7A]">{data.range?.start} → {data.range?.end}</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">Range</Label>
            <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
              <SelectTrigger className="bg-white mt-1 w-[160px]" data-testid="performance-range"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="1">Last 1 month</SelectItem>
                <SelectItem value="3">Last 3 months</SelectItem>
                <SelectItem value="6">Last 6 months</SelectItem>
                <SelectItem value="12">Last 12 months</SelectItem>
                <SelectItem value="24">Last 24 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="rounded-full" onClick={fetchData} data-testid="performance-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Grade summary */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-4 flex flex-wrap items-center gap-4" data-testid="performance-grade-card">
        <div className="flex items-center justify-center w-20 h-20 rounded-full text-4xl font-bold text-white shrink-0"
             style={{ backgroundColor: gradeColor, fontFamily: 'Fredoka, sans-serif' }}>
          {att.grade || '—'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wide text-[#6B6B7A]">Overall Grade</p>
          <p className="text-xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {att.grade === 'A' ? 'Excellent — keep it up!'
            : att.grade === 'B' ? 'Solid performer'
            : att.grade === 'C' ? 'Could improve punctuality'
            : att.grade === 'D' ? 'Needs immediate review'
            : 'Not enough shifts in this window'}
          </p>
          <p className="text-xs text-[#6B6B7A] mt-1">
            Based on attendance score {att.score_pct != null ? `${att.score_pct}%` : '—'} across {att.scheduled || 0} scheduled shifts.
          </p>
        </div>
      </div>

      {/* Primary attendance stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-[#E8E5DF] p-4">
            <p className="text-[10px] uppercase tracking-wide text-[#6B6B7A]">{s.label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color: s.accent }}>{s.value}</p>
            <p className="text-[10px] text-[#6B6B7A] mt-1">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {secondary.map((s) => (
          <div key={s.label} className="bg-[#FFFDF8] rounded-2xl border border-[#E8E5DF] p-4">
            <p className="text-[10px] uppercase tracking-wide text-[#6B6B7A]">{s.label}</p>
            <p className="text-xl font-bold text-[#1E1E24] mt-1">{s.value}</p>
            <p className="text-[10px] text-[#6B6B7A] mt-1">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Monthly breakdown — table */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <div className="px-4 py-2 border-b border-[#E8E5DF] flex items-center gap-2">
          <Award className="w-4 h-4 text-[#6B6B7A]" />
          <p className="font-semibold text-sm">Monthly Breakdown</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              <TableHead className="text-right">Hours</TableHead>
              <TableHead className="text-right">Shifts</TableHead>
              <TableHead className="text-right">Parties Hosted</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data.monthly || []).length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-6 text-xs text-[#6B6B7A]">No activity in this window.</TableCell></TableRow>
            ) : (data.monthly || []).map((m) => (
              <TableRow key={m.month} data-testid={`performance-month-${m.month}`}>
                <TableCell className="font-medium">{m.month}</TableCell>
                <TableCell className="text-right">{Number(m.hours || 0).toFixed(2)}h</TableCell>
                <TableCell className="text-right">{m.shifts || 0}</TableCell>
                <TableCell className="text-right">{m.parties || 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Recent parties */}
      {(data.recent_parties || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
          <div className="px-4 py-2 border-b border-[#E8E5DF] flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#6B6B7A]" />
            <p className="font-semibold text-sm">Recent Parties Hosted</p>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Child</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.recent_parties.map((p) => (
                <TableRow key={p.booking_id} data-testid={`performance-party-${p.booking_id}`}>
                  <TableCell>{p.party_date}</TableCell>
                  <TableCell>{p.child_name || '—'}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] capitalize">{p.status || 'confirmed'}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-[10px] text-[#6B6B7A] text-center">
        <AlertIcon className="w-3 h-3 inline mr-1" />
        Late = clock-in {'>'}10 min after scheduled start · No-show = no clock-in for a scheduled shift.
      </p>
    </div>
  );
};

// -------------------------------------------------------------------
// Messages tab
// -------------------------------------------------------------------
const MessagesTab = ({ employee }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [highPriority, setHighPriority] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchMsgs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/employee-messages/conversation`,
        { params: { employee_id: employee.user_id }, withCredentials: true });
      setMessages(r.data?.messages || []);
    } catch (e) {
      toast.error('Failed to load messages');
    } finally { setLoading(false); }
  }, [employee.user_id]);

  useEffect(() => { fetchMsgs(); }, [fetchMsgs]);

  const send = async () => {
    if (!body.trim()) { toast.error('Message body required'); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/admin/employee-messages/reply`, {
        employee_id: employee.user_id, body: body.trim(),
        subject: subject.trim() || undefined, high_priority: highPriority,
      }, { withCredentials: true });
      toast.success('Message sent');
      setBody(''); setSubject(''); setHighPriority(false);
      fetchMsgs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-3" data-testid="emp-messages-tab">
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <div className="px-4 py-2 border-b border-[#E8E5DF] flex items-center justify-between">
          <p className="font-semibold text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#6B6B7A]" /> Conversation</p>
          <Button variant="outline" size="sm" onClick={fetchMsgs} className="rounded-full"><RefreshCw className="w-3 h-3" /></Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto p-4 space-y-2 bg-[#FFFDF8]">
          {loading ? <p className="text-xs text-[#6B6B7A] text-center py-4">Loading…</p>
          : messages.length === 0 ? <p className="text-xs text-[#6B6B7A] text-center py-8">No messages yet — start the conversation below.</p>
          : messages.map((m) => {
            const fromAdmin = m.direction === 'from_admin';
            return (
              <div key={m.message_id}
                   className={`max-w-[80%] ${fromAdmin ? 'ml-auto bg-[#FF3B6B] text-white' : 'mr-auto bg-white border border-[#E8E5DF] text-[#1E1E24]'} rounded-2xl px-3 py-2 text-sm`}
                   data-testid={`msg-${m.message_id}`}>
                {m.subject && <p className="font-semibold text-xs opacity-80 mb-1">{m.subject}</p>}
                <p className="whitespace-pre-wrap">{m.body}</p>
                <p className={`text-[10px] mt-1 ${fromAdmin ? 'text-white/70' : 'text-[#6B6B7A]'}`}>
                  {new Date(m.created_at).toLocaleString()}
                  {m.high_priority && ' · 🔥 high priority'}
                </p>
              </div>
            );
          })}
        </div>
        <div className="border-t border-[#E8E5DF] p-3 space-y-2 bg-white">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (optional)" className="text-xs" data-testid="msg-subject" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder={`Message to ${employee.name}…`} data-testid="msg-body" />
          <div className="flex items-center justify-between">
            <label className="text-xs flex items-center gap-2">
              <input type="checkbox" checked={highPriority} onChange={(e) => setHighPriority(e.target.checked)} data-testid="msg-priority" /> High priority
            </label>
            <Button onClick={send} disabled={sending || !body.trim()} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="msg-send">
              <Send className="w-4 h-4 mr-1" /> {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------
// Main page
// -------------------------------------------------------------------
export const CrmEmployeeDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);

  const fetchEmployee = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/employees/${userId}`, { withCredentials: true });
      setEmployee(r.data);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error('Employee not found');
        navigate('/crm/employees');
      } else {
        toast.error('Failed to load employee');
      }
    } finally { setLoading(false); }
  }, [userId, navigate]);

  useEffect(() => { fetchEmployee(); }, [fetchEmployee]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;
  if (!employee) return null;

  return (
    <div data-testid="crm-employee-detail">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <Link to="/crm/employees" className="text-[#6B6B7A] hover:text-[#1E1E24]" data-testid="emp-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {employee.name}
            </h1>
            <p className="text-[#6B6B7A] text-sm">
              {employee.employee_number && (
                <span className="font-semibold text-[#1E1E24]" data-testid="emp-header-employee-number">{employee.employee_number}</span>
              )}
              {employee.employee_number && ' · '}
              {ROLE_LABELS[employee.employee_role] || employee.employee_role || 'Staff'}
              {employee.employment_type && ` · ${EMPLOYMENT_LABELS[employee.employment_type] || employee.employment_type}`}
              {' · '}{employee.email}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {employee.is_clocked_in && <Badge className="bg-green-100 text-green-700 border-green-200">🟢 Clocked In</Badge>}
          {!employee.active && <Badge className="bg-gray-200 text-gray-700">Inactive</Badge>}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map((t) => {
          const Icon = t.icon;
          const count = t.key === 'payroll' ? employee.counts?.paystubs
                      : t.key === 'messages' ? employee.counts?.messages
                      : t.key === 'time' ? employee.counts?.shifts_upcoming : null;
          return (
            <Button key={t.key}
              onClick={() => setActiveTab(t.key)}
              variant={activeTab === t.key ? 'default' : 'outline'}
              className={`rounded-full ${activeTab === t.key ? 'bg-[#FF3B6B] text-white' : 'border-[#E8E5DF] bg-white'}`}
              data-testid={`emp-tab-${t.key}`}>
              <Icon className="w-4 h-4 mr-1" /> {t.label}
              {count ? <span className={`ml-2 px-1.5 py-0.5 rounded-full text-[10px] ${activeTab === t.key ? 'bg-white/20' : 'bg-[#FF3B6B]/10 text-[#FF3B6B]'}`}>{count}</span> : null}
            </Button>
          );
        })}
      </div>

      {/* Active tab */}
      {activeTab === 'profile' && <ProfileTab employee={employee} onUpdated={fetchEmployee} />}
      {activeTab === 'time' && <TimeTab employee={employee} />}
      {activeTab === 'payroll' && <PayrollTab employee={employee} />}
      {activeTab === 'performance' && <PerformanceTab employee={employee} />}
      {activeTab === 'messages' && <MessagesTab employee={employee} />}
    </div>
  );
};

export default CrmEmployeeDetail;
